/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useCallback, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { useDashboardStore } from "@/stores/useDashboardStore";
import Typography from "@mui/material/Typography";

import { useReleaseFeatureToggle } from "../../../../../shared/featureToggles/hooks";
import { FEATURE_NAMES } from "../../../../../shared/featureToggles/types";
import CardWrapper from "../../../CardWrapper/CardWrapper";
import { useStyles as dashboardStyles } from "../../../styles";
import CreateNewsButton from "../components/CreateNewsButton";
import SingleNews from "../components/SingleNews/SingleNews";
import useNews from "../hooks/useNews";
import { useStyles } from "../styles";
import { NewsDto } from "../types";

const NewsVertical = () => {
  const { isReleaseFeatureEnabledFromBE } = useReleaseFeatureToggle();
  const { isLoading: isNewsLoading } = useNews();

  const { t } = useTranslation();

  const { newsData, isDashboardLoading, newsError, setUnpublishedReleaseNote } =
    useDashboardStore();

  const { emptyNews } = useStyles();
  const { scrollableCardContent } = dashboardStyles();

  const isLoading = isDashboardLoading || isNewsLoading;
  const isEmpty = newsData?.length === 0;

  useEffect(() => {
    checkForUnpublishedReleaseNote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsData]);

  const checkForUnpublishedReleaseNote = useCallback(() => {
    newsData &&
      setUnpublishedReleaseNote(
        newsData?.filter(
          (news: NewsDto) => news.releaseNotes && news.draft === true
        )
      );
  }, [newsData, setUnpublishedReleaseNote]);

  const renderNewsList = useCallback(() => {
    return newsData?.map((news: NewsDto) => (
      <SingleNews
        key={news.id}
        newsData={news}
        isReleaseNotes={news.releaseNotes}
      />
    ));
  }, [newsData]);

  return (
    <CardWrapper
      titleLoaderTestId="news-vertical-title-loader"
      cardTitle={t("Dashboard##news")}
      titleInlineAction={
        isReleaseFeatureEnabledFromBE(FEATURE_NAMES.ManageNews) && (
          <CreateNewsButton />
        )
      }
      isLoading={isLoading}
      isError={newsError}
      isNews
    >
      {!isLoading && newsData && (
        <div css={css(isEmpty ? emptyNews : scrollableCardContent)}>
          {isEmpty ? (
            <Typography variant="body1Medium">
              {t("Dashboard##no news")}
            </Typography>
          ) : (
            renderNewsList()
          )}
        </div>
      )}
    </CardWrapper>
  );
};

export default NewsVertical;
